import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useAccessToken,
    useCommerceApi,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {useLocation} from 'react-router-dom'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {useGlobaleContext} from '../../../../contexts/globale'
import ProductViewModal from '../../../product-view-modal'
import LoadingSpinner from '../../../loading-spinner'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '../../../../hooks/use-toast'
import {useIntl} from 'react-intl'
import {GWP_PROMOTION_COOKIE_ID} from '../../../../constants'
import {getPriceData} from '../../../../utils/product-utils'
import useTagManager from '../../../../hooks/use-tag-manager'
import useMonetate from '../../../../hooks/use-monetate'
import {useWishList} from '../../../../hooks/use-wish-list'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAddToCartDrawerContext} from '../../../../hooks/use-add-to-cart-drawer'
import {useSiteContext} from '../../../../contexts/site'
import {scheduleIdleAnalytics, yieldToMain} from '../../../../utils/common-utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../../../utils/log-basket-error-to-ssr'
const GWP_PROMOTION_REGEX = /gwp/i

/**
 * MonetateListener
 *
 * Renders a Monetate-powered product recommendations carousel on the PDP.
 * Handles quick-add-to-cart flow including GWP (Gift With Purchase) promotion
 * logic, Monetate tracking, and Tag Manager events.
 */
const PdpMonetateQuickAdd = ({request, typeId, name}) => {
    // ─── Analytics ───────────────────────────────────────────────────────────
    const tagManager = useTagManager()
    const monetate = useMonetate()

    const analyticsProps = {
        component_id: typeId,
        component_name: name || typeId
    }

    // ─── Customer & Wishlist ─────────────────────────────────────────────────
    const {data: wishlist} = useWishList()
    const {data: customer} = useCurrentCustomer()

    // ─── Site & Globale Context ──────────────────────────────────────────────
    const {setOpenBonusModal} = useSiteContext()
    const {countryISO, currencyCode, globaleCurrency} = useGlobaleContext()

    // ─── Commerce API ────────────────────────────────────────────────────────
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()

    // ─── Routing ─────────────────────────────────────────────────────────────
    const location = useLocation()

    // ─── Messaging ────────────────────────────────────────────────────────────────
    const {formatMessage} = useIntl()

    // ─── Local State ─────────────────────────────────────────────────────────
    const [tileProduct, setTileProduct] = useState()
    const [showSpinner, setShowSpinner] = useState(false)
    const tileProductIndexRef = useRef()

    // ─── Toast ───────────────────────────────────────────────────────────────
    const showToast = useToast()

    const showError = (message, statusCode) => {
        showToast({
            title: statusCode === '403' ? message : formatMessage(message),
            status: 'error'
        })
    }

    // ─── Drawer & Modal ──────────────────────────────────────────────────────
    const {
        isOpen: isAddToCartDrawerOpen,
        onOpen: onAddToCartDrawerOpen,
        onClose: onAddToCartDrawerClose
    } = useAddToCartDrawerContext()

    const {
        isOpen: isQuickAddModalOpen,
        onOpen: onQuickAddModalOpen,
        onClose: onQuickAddModalClose
    } = useDisclosure()

    // ─── Handlers ────────────────────────────────────────────────────────────

    /**
     * Fetches full product data by ID and opens the Quick Add modal.
     * Expands all relevant product associations (images, prices, variations, etc.).
     */
    const fetchProduct = async (productId, indexPosition) => {
        setShowSpinner(true)
        tileProductIndexRef.current = indexPosition
        const token = await getTokenWhenReady()

        const requestParams = {
            id: productId,
            perPricebook: true,
            expand: [
                'availability',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
                'set_products',
                'page_meta_tags',
                'bundled_products'
            ],
            allImages: true,
            c_selectedCountry: countryISO,
            c_selectedCurrency: currencyCode
        }
        try {
            const product = await api.shopperProducts.getProduct({
                parameters: requestParams,
                headers: {
                    Authorization: `Bearer ${token}`
                },
                keepPreviousData: true
            })
            onQuickAddModalOpen(true)
            // TODO What to happen if we have one SKU only.
            setTileProduct(product)
            setShowSpinner(false)
        } catch (e) {
            setShowSpinner(false)
            console.error('Error occurred in fetching and opening a modal', e.message)
        }
    }

    /**
     * Handles all side-effects after a successful add-to-cart action:
     * - GWP promotion cookie check & bonus modal trigger
     * - Add-to-cart drawer open (when no GWP promo active)
     * - Monetate page tracking
     * - Tag Manager add-to-cart event
     */
    const addToBagActions = async (variant) => {
        try {
            const indexPosition = tileProductIndexRef.current
            const itemsAdded = await addItemToNewOrExistingBasket([
                {
                    productId: variant.productId,
                    price: variant.price,
                    quantity: 1
                }
            ])
            let ga4ItemsAdded = []

            if (itemsAdded) {
                // Prioritise GWP promotions as per business requirement
                const promoId = (
                    itemsAdded?.bonusDiscountLineItems?.find((i) => /gwp/i?.test(i.promotionId)) ||
                    itemsAdded?.bonusDiscountLineItems?.[0]
                )?.promotionId

                const cookie =
                    onClient() &&
                    new CookieStorage({
                        keySuffix: '',
                        sharedContext: !onClient()
                    })

                const gwpPromotionState =
                    onClient() && cookie ? cookie.get(GWP_PROMOTION_COOKIE_ID) : null

                const selectedSku = {
                    ...tileProduct,
                    ...variant
                }
                ga4ItemsAdded = [
                    {
                        product: selectedSku,
                        variant,
                        custom: {
                            price: variant?.price,
                            quantity: 1
                        }
                    }
                ]

                const priceData = getPriceData(selectedSku)

                // Only open the drawer when there is no active GWP promotion awaiting acknowledgement
                if (!(promoId && gwpPromotionState !== 'closed')) {
                    const selectedImage = document
                        .querySelector(
                            "div[data-testid='product-view-modal'] .img-wrapper .cloudinary-image"
                        )
                        ?.getAttribute('src')
                    onAddToCartDrawerOpen({
                        product: selectedSku,
                        itemsAdded,
                        selectedQuantity: 1,
                        priceData,
                        ...(selectedImage && {heroImage: selectedImage})
                    })
                }

                setOpenBonusModal(true)
            }

            scheduleIdleAnalytics(() => {
                try {
                    if (itemsAdded) {
                        monetate.monetateTrackPage(
                            'addToBagModal',
                            window.mtBreadcrumb,
                            itemsAdded?.productItems,
                            customer?.customerType === 'registered',
                            wishlist?.customerProductListItems?.length || 0,
                            null,
                            null,
                            [
                                {
                                    masterId:
                                        (tileProduct?.hitType === 'master'
                                            ? tileProduct?.productId
                                            : '') ||
                                        tileProduct?.master?.masterId ||
                                        tileProduct?.representedProduct?.c_masterVariantID ||
                                        tileProduct?.id,
                                    variantId: variant?.type?.master
                                        ? variant?.variants?.[0]?.productId
                                        : variant?.productId || variant?.id
                                }
                            ],
                            itemsAdded?.giftCertificateItems
                        )
                    }

                    tagManager.addToCart(
                        tileProduct,
                        tileProduct,
                        null,
                        null,
                        'recommender',
                        null,
                        false,
                        {},
                        false,
                        false,
                        false,
                        analyticsProps
                    )
                    tagManager.addToCartGA4({
                        itemsAdded: ga4ItemsAdded,
                        method: 'recommender',
                        currency: globaleCurrency,
                        sorting: undefined,
                        indexPosition,
                        listName: 'recommended for you',
                        listId: 'Recommended For You'
                    })
                } catch (error) {
                    console.error('Error occurred in deferred add-to-bag analytics', error.message)
                }
            })
            setTimeout(() => {
                onQuickAddModalClose()
            }, 3000)
        } catch (error) {
            const errResponse = error?.response ? await error?.response?.json() : null
            logger.error('ATCXL Error adding product to basket', {
                namespace: 'MonetateListener.addToBagActions',
                additionalProperties: {error, errResponse, productId: variant?.productId}
            })
            logBasketErrorToSsr('MonetateListener.addToBagActions', {
                error,
                errResponse,
                productId: variant?.productId
            })

            if (errResponse?.statusCode === '403') {
                showError(errResponse.statusMessage, errResponse.statusCode)
            } else {
                showError(API_ERROR_MESSAGE)
            }

            setShowSpinner(false)
            throw error
        }
    }

    // ─── Effects ─────────────────────────────────────────────────────────────

    // Close the add-to-cart drawer on route change
    useEffect(() => {
        if (isAddToCartDrawerOpen) {
            onAddToCartDrawerClose()
        }
    }, [location.pathname])

    useEffect(() => {
        if (request?.productId) {
            fetchProduct(request.productId, request.indexPosition)
        }
    }, [request?.requestId])

    // ─── Render ──────────────────────────────────────────────────────────────

    return (
        <>
            {showSpinner && <LoadingSpinner position="fixed" top="auto" />}
            {isQuickAddModalOpen && (
                <ProductViewModal
                    isOpen={isQuickAddModalOpen}
                    onOpen={onQuickAddModalOpen}
                    onClose={onQuickAddModalClose}
                    isQuickAddModal={true}
                    product={tileProduct}
                    showDeliveryOptions={false}
                    updateCart={async (variant) => {
                        setShowSpinner(true)
                        await addToBagActions(variant)
                        setShowSpinner(false)
                    }}
                />
            )}
        </>
    )
}

PdpMonetateQuickAdd.propTypes = {
    request: PropTypes.shape({
        productId: PropTypes.string,
        indexPosition: PropTypes.number,
        requestId: PropTypes.number
    }),
    typeId: PropTypes.string,
    name: PropTypes.any
}

/** Returns the id of the promotion to act on, giving GWP promotions priority. */
const getPriorityPromoId = (basket) => {
    const bonusItems = basket?.bonusDiscountLineItems
    return (bonusItems?.find((i) => GWP_PROMOTION_REGEX.test(i.promotionId)) || bonusItems?.[0])
        ?.promotionId
}

/** True when a bonus promotion was triggered and the shopper hasn't dismissed it yet. */
const isGwpPromotionPending = (basket) => {
    if (!getPriorityPromoId(basket)) return false

    const state = onClient()
        ? new CookieStorage({keySuffix: '', sharedContext: false}).get(GWP_PROMOTION_COOKIE_ID)
        : null
    return state !== 'closed'
}

/** Reads the SKU from the Monetate merch item that contains the given element. */
const getMerchItemSku = (element) => element?.closest('.mt-merch-item')?.getAttribute('data-sku')

export const MonetateListener = ({ID, typeId, name, setShowSpinner}) => {
    const {countryISO, currencyCode} = useGlobaleContext()
    const intl = useIntl()
    const location = useLocation()
    const showToast = useToast()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {
        isOpen: isAddToCartDrawerOpen,
        onOpen: onAddToCartDrawerOpen,
        onClose: onAddToCartDrawerClose
    } = useAddToCartDrawerContext()
    const {data: customer} = useCurrentCustomer()
    const wishlist = useWishList()

    const requestIdRef = useRef(0)
    const [quickAddRequest, setQuickAddRequest] = useState(null)

    const showError = (error) => {
        const isForbidden = error?.statusCode === '403'

        showToast({
            title: isForbidden ? error.statusMessage : intl.formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    const handleAddToBag = async (product, addableId) => {
        if (!addableId) return

        try {
            const itemsAdded = await addItemToNewOrExistingBasket([
                {productId: addableId, quantity: 1}
            ])
            document.querySelector('.mt-ctl-flyout .mt-ctl-close').click()
            await yieldToMain()
            if (!isGwpPromotionPending(itemsAdded)) {
                onAddToCartDrawerOpen({
                    product,
                    itemsAdded,
                    selectedQuantity: 1,
                    priceData: getPriceData(product)
                })
            }

            monetate.monetateTrackPage(
                'addToBagModal',
                window.mtBreadcrumb,
                itemsAdded?.productItems,
                customer?.customerType === 'registered',
                wishlist?.customerProductListItems?.length || 0,
                null,
                null,
                [{masterId: product?.master?.masterId, variantId: product?.id}],
                itemsAdded?.giftCertificateItems
            )
            tagManager.addToCart(
                product,
                product,
                null,
                null,
                'pdp',
                null,
                false,
                null,
                null,
                null,
                false,
                {}
            )
        } catch (error) {
            showError(error)
        }
    }

    // The click listener below is registered once, so it calls the latest handler via a ref
    // to avoid acting on stale customer / wishlist data.
    const handleAddToBagRef = useRef(handleAddToBag)
    handleAddToBagRef.current = handleAddToBag

    // Monetate renders the "add to bag" buttons outside React, so listen at the document level.
    useEffect(() => {
        const handleClick = async (event) => {
            const button = event?.target?.closest('.mt-complete-the-look .mt-add-to-bag-btn')
            if (!button) return
            const productId = getMerchItemSku(button)
            if (!productId) return
            event.preventDefault()
            try {
                setShowSpinner(true)
                const token = await getTokenWhenReady()
                const product = await api.shopperProducts.getProduct({
                    parameters: {
                        id: productId,
                        c_selectedCountry: document
                            .querySelector('.monetate-recs')
                            ?.getAttribute('data-country'),
                        perPricebook: true,
                        c_selectedCurrency: document
                            .querySelector('.monetate-recs')
                            ?.getAttribute('data-currency'),
                        expand: ['availability', 'promotions', 'images', 'prices', 'variations']
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    keepPreviousData: true
                })
                await handleAddToBagRef.current(product, productId)
                setShowSpinner(false)
            } catch (error) {
                const errResponse = error?.response ? await error?.response?.json() : null
                if (errResponse?.statusCode === '403') {
                    showError(errResponse.statusMessage, errResponse.statusCode)
                } else {
                    showError(API_ERROR_MESSAGE)
                }
                setShowSpinner(false)
                console.error('Error occurred in add to bag from carousel', error.message)
            }
        }

        document.addEventListener('click', handleClick)
        return () => document.removeEventListener('click', handleClick)
    }, [])

    const handleQuickAddClick = (event) => {
        const target = event.target?.closest ? event.target : event.target?.parentElement
        const quickAddButton = target?.closest('.mt-quick-add-btn')
        if (!quickAddButton) return

        const productId = getMerchItemSku(quickAddButton)
        if (!productId) return

        const slideIndex = Number(quickAddButton.closest('[data-slide]')?.dataset.slide)
        requestIdRef.current += 1
        setQuickAddRequest({
            productId,
            indexPosition: Number.isInteger(slideIndex) ? slideIndex + 1 : undefined,
            requestId: requestIdRef.current
        })
    }

    useEffect(() => {
        if (isAddToCartDrawerOpen) {
            onAddToCartDrawerClose()
        }
    }, [location?.pathname])

    useEffect(() => {
        if (isAddToCartDrawerOpen) {
            document.querySelector('html').style.overflow = 'hidden'
        } else {
            document.querySelector('html').style.overflow = ''
        }
    }, [isAddToCartDrawerOpen])

    if (!ID) return <></>

    return (
        <Box
            className={`monetate-recs ${name}`}
            data-country={countryISO}
            sx={{
                contentVisibility: 'auto',
                containIntrinsicSize: 'auto 600px'
            }}
            onClick={handleQuickAddClick}
            data-currency={currencyCode}
        >
            <Box
                id={ID}
                className={ID}
                width="100%"
                sx={{
                    contain: 'layout paint style',
                    isolation: 'isolate'
                }}
            />
            {quickAddRequest && (
                <PdpMonetateQuickAdd request={quickAddRequest} typeId={typeId} name={name} />
            )}
        </Box>
    )
}

MonetateListener.propTypes = {
    ID: PropTypes.string,
    typeId: PropTypes.string,
    setShowSpinner: PropTypes.any,
    name: PropTypes.any
}

export default MonetateListener
